import { AxiosError, AxiosResponse } from 'axios'
import { RouteParameters, ToastSubType } from '../../../../constants'
import {
  AvailabilityConstraint,
  AvailabilityConstraintCreateRequest,
  AvailabilityConstraintDeleteRequest,
  AvailabilityConstraintDeleteResponse,
  AvailabilityConstraintHistoriesGetRequest,
  AvailabilityConstraintHistory,
  AvailabilityConstraintPageResponse,
  AvailabilityConstraintsGetRequest,
  AvailabilityConstraintUpdateRequest,
  PaginatedResponse,
} from '../../../../types'
import { hydratePathname } from '../../../../util'
import { HttpClientConfig } from '../../../useHttpClientConfig'
import { ToastNotifyFunction } from '../../../useNotification'
import { apiVersion, service } from '../service'

interface ResponseData<T> {
  data: T
}

const Routes = Object.freeze({
  LIST: `/api/${apiVersion}/resources/:${RouteParameters.RESOURCE_ID}/availability-constraints`,
  LIST_HISTORIES: `/api/${apiVersion}/resources/:${RouteParameters.RESOURCE_ID}/availability-constraint-histories`,
  ONE: `/api/${apiVersion}/resources/:${RouteParameters.RESOURCE_ID}/availability-constraints/:${RouteParameters.AVAILABILITY_CONSTRAINT_ID}`,
})

const makeCreateAvailabilityConstraint =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async (resourceId: string, request: AvailabilityConstraintCreateRequest): Promise<AvailabilityConstraint> => {
    const pathname = hydratePathname(Routes.LIST, { [RouteParameters.RESOURCE_ID]: resourceId })

    const { data } = await service(baseUrl)
      .post<AvailabilityConstraintCreateRequest, AxiosResponse<ResponseData<AvailabilityConstraint>>>(pathname, request, config)
      .catch((error: AxiosError) => {
        if (error.code !== 'ECONNABORTED') {
          return Promise.reject(error)
        }

        notify(
          {
            message: `Your attempt to create an availability constraint took too long. It might still have been created. Please check in the calendar or list tab to check if it worked before trying again.`,
            type: ToastSubType.AVAILABILITY_CONSTRAINT_CREATE_TIMEOUT,
          },
          'error'
        )

        return Promise.reject(error)
      })

    return data.data
  }

const makeDeleteAvailabilityConstraint =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async ({ id, resourceId }: AvailabilityConstraintDeleteRequest): Promise<AvailabilityConstraintDeleteResponse> => {
    const pathname = hydratePathname(Routes.ONE, {
      [RouteParameters.RESOURCE_ID]: resourceId,
      [RouteParameters.AVAILABILITY_CONSTRAINT_ID]: id,
    })

    const { data } = await service(baseUrl)
      .delete<string, AxiosResponse<AvailabilityConstraintDeleteResponse>>(pathname, config)
      .catch((error: AxiosError) => {
        if (error.code !== 'ECONNABORTED') {
          return Promise.reject(error)
        }

        notify(
          {
            message: `Your attempt to delete an availability constraint took too long. It might still have been deleted. Please check in your calendar or list view to check if it worked before trying again.`,
            type: ToastSubType.AVAILABILITY_CONSTRAINT_DELETE_TIMEOUT,
          },
          'error'
        )

        return Promise.reject(error)
      })

    return data
  }

const makeGetAvailabilityConstraints =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async ({ resource_id }: AvailabilityConstraintsGetRequest): Promise<AvailabilityConstraint[]> => {
    const pathname = hydratePathname(Routes.LIST, {
      [RouteParameters.RESOURCE_ID]: resource_id,
    })

    const { data } = await service(baseUrl)
      .get<AvailabilityConstraintPageResponse>(pathname, config)
      .catch((error: AxiosError) => {
        if (error.code !== 'ECONNABORTED') {
          return Promise.reject(error)
        }

        notify(
          {
            message: `Your attempt to retrieve availability constraints took too long; please try again.`,
            type: ToastSubType.AVAILABILITY_CONSTRAINT_GET_TIMEOUT,
          },
          'error'
        )

        return Promise.reject(error)
      })

    return data.data
  }

const makeGetAvailabilityConstraintHistories =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async (request: AvailabilityConstraintHistoriesGetRequest): Promise<PaginatedResponse<AvailabilityConstraintHistory>> => {
    const { resource_id, ...searchParams } = request

    const pathname = hydratePathname(Routes.LIST_HISTORIES, {
      [RouteParameters.RESOURCE_ID]: resource_id,
    })

    const { data } = await service(baseUrl)
      .get<PaginatedResponse<AvailabilityConstraintHistory>>(pathname, { ...config, params: searchParams })
      .catch((error: AxiosError) => {
        if (error.code !== 'ECONNABORTED') {
          return Promise.reject(error)
        }

        notify(
          {
            message: `Your attempt to retrieve availability constraint histories took too long; please try again.`,
            type: ToastSubType.AVAILABILITY_CONSTRAINT_HISTORIES_GET_TIMEOUT,
          },
          'error'
        )

        return Promise.reject(error)
      })

    return data
  }

const makeUpdateAvailabilityConstraint =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async (resourceId: string, id: string, request: AvailabilityConstraintUpdateRequest): Promise<AvailabilityConstraint> => {
    const pathname = hydratePathname(Routes.ONE, {
      [RouteParameters.RESOURCE_ID]: resourceId,
      [RouteParameters.AVAILABILITY_CONSTRAINT_ID]: id,
    })

    const { data } = await service(baseUrl)
      .patch<AvailabilityConstraintUpdateRequest, AxiosResponse<ResponseData<AvailabilityConstraint>>>(pathname, request, config)
      .catch((error: AxiosError) => {
        if (error.code !== 'ECONNABORTED') {
          return Promise.reject(error)
        }

        notify(
          {
            message: `Your attempt to update an availability constraint took too long. It might still have been updated. Please check in your calendar or list view to check if it worked before trying again.`,
            type: ToastSubType.AVAILABILITY_CONSTRAINT_UPDATE_TIMEOUT,
          },
          'error'
        )

        return Promise.reject(error)
      })

    return data.data
  }

export {
  makeCreateAvailabilityConstraint,
  makeDeleteAvailabilityConstraint,
  makeGetAvailabilityConstraintHistories,
  makeGetAvailabilityConstraints,
  makeUpdateAvailabilityConstraint,
}
